import { useEffect, useRef, useState, PointerEvent } from 'react';
import { useDragControls } from 'framer-motion';
import { motion } from 'framer-motion';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../../utils/common-utils.ts';

interface IProps {
  show: boolean;
  onClose: () => void;
}

type DesmosGraphingCalculator = {
  destroy: () => void;
  resize: () => void;
  focusFirstExpression: () => void;
  setMathBounds: (bounds: {
    left: number;
    right: number;
    bottom: number;
    top: number;
  }) => void;
};

type DesmosScientificCalculator = {
  destroy: () => void;
  resize: () => void;
};

type DesmosApi = {
  GraphingCalculator: (
    element: HTMLElement,
    options: {
      expressionsCollapsed: boolean;
      keypadActivated: boolean;
      allowKeypadToBeDismissedWhenNarrow: boolean;
      notes: boolean;
      images: boolean;
      folders: boolean;
    },
  ) => DesmosGraphingCalculator;
  ScientificCalculator: (
    element: HTMLElement,
  ) => DesmosScientificCalculator;
};

declare global {
  interface Window {
    Desmos: DesmosApi;
  }
}

type Tab = 'graphing' | 'scientific';

const DesmosCalculator = ({ show, onClose }: IProps) => {
  const controls = useDragControls();
  const [isLoad, setIsLoad] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('graphing');
  const [hoveredTab, setHoveredTab] = useState<Tab | null>(null);
  const graphingRef = useRef<DesmosGraphingCalculator | null>(null);
  const scientificRef = useRef<DesmosScientificCalculator | null>(null);
  const constraintsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.desmos.com/api/v1.11/calculator.js?apiKey=${import.meta.env.VITE_DESMOS_API_KEY}`;
    document.body.appendChild(script);
    script.onload = () => setIsLoad(true);
    return () => {
      document.body.removeChild(script);
      setIsLoad(false);
    };
  }, []);

  useEffect(() => {
    if (!show || !isLoad) {
      graphingRef.current?.destroy();
      scientificRef.current?.destroy();
      graphingRef.current = null;
      scientificRef.current = null;
      return;
    }
    const graphingElement = document.getElementById('desmos-graphing');
    const scientificElement = document.getElementById('desmos-scientific');

    if (!graphingElement || !scientificElement) {
      return;
    }

    graphingRef.current = window.Desmos.GraphingCalculator(graphingElement, {
      expressionsCollapsed: false,
      keypadActivated: true,
      allowKeypadToBeDismissedWhenNarrow: true,
      notes: false,
      images: false,
      folders: false,
    });
    graphingRef.current.setMathBounds({ left: -10, right: 10, bottom: -15, top: 15 });
    graphingRef.current.focusFirstExpression();
    scientificRef.current =
      window.Desmos.ScientificCalculator(scientificElement);
  }, [show, isLoad]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      graphingRef.current?.resize();
      scientificRef.current?.resize();
    }, 50);
  };

  const dragHandler = (e: PointerEvent<HTMLElement>) => {
    if (e.currentTarget.id === 'drag-header') controls.start(e);
  };

  if (!show || !isLoad) return null;
  return (
    <div ref={constraintsRef} className="pointer-events-none fixed inset-0" style={{ zIndex: 60 }}>
    <motion.div
      drag
      dragListener={false}
      dragControls={controls}
      dragMomentum={false}
      dragConstraints={constraintsRef}
      dragElastic={0}
      className={cn(
        'pointer-events-auto absolute left-4 top-4 w-full select-none',
        expanded ? 'max-w-7xl' : 'max-w-sm',
      )}
    >
      <div className={'overflow-hidden rounded-xl shadow-lg'} style={{ border: '1px solid #374151' }}>
        <div
          id={'drag-header'}
          className={'flex cursor-move items-center justify-between px-3 py-2'}
          style={{ backgroundColor: '#1f2937', borderBottom: '1px solid #374151' }}
          onPointerDown={dragHandler}
        >
          <div className={'flex cursor-default'}>
            {(['graphing', 'scientific'] as Tab[]).map((tab, i) => {
              const isActive = activeTab === tab;
              const isHovered = hoveredTab === tab;
              const bg = isHovered ? '#2563eb' : isActive ? '#ffffff' : '#1f2937';
              const color = isActive && !isHovered ? '#1f2937' : '#ffffff';
              return (
                <button
                  key={tab}
                  style={{
                    backgroundColor: bg,
                    color,
                    border: isHovered ? '1px solid #ffffff' : '1px solid #4b5563',
                    borderLeft: i === 1 ? 'none' : isHovered ? '1px solid #ffffff' : '1px solid #4b5563',
                    borderRadius: i === 0 ? '9999px 0 0 9999px' : '0 9999px 9999px 0',
                    padding: '4px 14px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    transition: 'background-color 0.15s',
                  }}
                  onClick={() => handleTabChange(tab)}
                  onMouseEnter={() => setHoveredTab(tab)}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  {tab === 'graphing' ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <line x1="9" y1="2" x2="9" y2="16" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5"/>
                      <line x1="2" y1="9" x2="16" y2="9" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.5"/>
                      <line x1="5" y1="2" x2="5" y2="16" stroke="currentColor" strokeWidth="0.4" strokeOpacity="0.3"/>
                      <line x1="13" y1="2" x2="13" y2="16" stroke="currentColor" strokeWidth="0.4" strokeOpacity="0.3"/>
                      <line x1="2" y1="5" x2="16" y2="5" stroke="currentColor" strokeWidth="0.4" strokeOpacity="0.3"/>
                      <line x1="2" y1="13" x2="16" y2="13" stroke="currentColor" strokeWidth="0.4" strokeOpacity="0.3"/>
                      <path d="M2 13 C5 13 6 5 9 5 C12 5 13 13 16 13" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="2" y="1" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.3"/>
                      <rect x="4" y="3" width="10" height="4" rx="1" stroke="currentColor" strokeWidth="1"/>
                      <rect x="3.5" y="9" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                      <rect x="7.5" y="9" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                      <rect x="11.5" y="9" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                      <rect x="3.5" y="12.5" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                      <rect x="7.5" y="12.5" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                      <rect x="11.5" y="12.5" width="2.5" height="2" rx="0.4" fill="currentColor"/>
                    </svg>
                  )}
                  {tab === 'graphing' ? 'Graphing' : 'Scientific'}
                </button>
              );
            })}
          </div>
          <div className={'flex flex-1 justify-center'}>
            <div style={{ color: '#9ca3af', display: 'grid', gridTemplateColumns: 'repeat(3, 5px)', gap: '4px', cursor: 'grab', padding: '6px', borderRadius: '6px', backgroundColor: 'transparent' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#374151')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'currentColor' }} />
              ))}
            </div>
          </div>
          <div className={'flex items-center gap-2'} style={{ color: '#d1d5db' }}>
            <button
              className="flex items-center"
              style={{ color: 'inherit' }}
              onClick={() => setExpanded((expanded) => !expanded)}
            >
              {expanded ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
            <button onClick={onClose} className={'cursor-pointer'} style={{ color: 'inherit' }}>
              <XMarkIcon className={'h-5 w-5'} />
            </button>
          </div>
        </div>
        <div style={{ position: 'relative', height: expanded ? '40rem' : activeTab === 'scientific' ? '22rem' : '38rem' }}>
          <div
            id="desmos-graphing"
            style={{ position: 'absolute', inset: 0, display: activeTab === 'graphing' ? 'block' : 'none' }}
          ></div>
          <div
            id="desmos-scientific"
            style={{ position: 'absolute', inset: 0, display: activeTab === 'scientific' ? 'block' : 'none' }}
          ></div>
        </div>
      </div>
    </motion.div>
    </div>
  );
};

export default DesmosCalculator;
